/**
 * GhlCalendarSelectionPanel — extension panel for the integrations hub (GHL block)
 * that lists the client's GHL calendars with "count into Rozmowy" checkboxes (VP-168).
 *
 * Semantics of analytics_settings.conversationCalendarIds:
 *  - null  → all calendars counted (legacy default; new calendars auto-included),
 *  - []    → none counted (GHL conversations = 0 everywhere),
 *  - [ids] → only these calendars counted.
 * When the user re-checks every calendar we store null (not the full list) so a
 * calendar added later in GHL is counted by default instead of silently excluded.
 *
 * Selection is derived from the settings query (no mirrored local state); toggles
 * update the query cache optimistically and roll back on a failed PATCH. Renders an
 * error hint when either fetch fails — a broken settings read must NOT fall back to
 * "all checked", or the first toggle would overwrite the stored subset.
 */
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2 } from "lucide-react";

type GhlCalendarInfo = {
  id: string;
  name: string;
  locationId?: string;
  locationName?: string | null;
};

type AnalyticsSettingsSlice = {
  conversationCalendarIds?: string[] | null;
};

export function GhlCalendarSelectionPanel({ forUserId }: { forUserId?: string | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fuParam = forUserId ? `?forUserId=${encodeURIComponent(forUserId)}` : "";
  const settingsUrl = forUserId
    ? `/api/analytics-settings?forClientUserId=${encodeURIComponent(forUserId)}`
    : "/api/analytics-settings";
  const settingsKey = ["analytics-settings", forUserId ?? ""];
  // Dashboard caches the same endpoint under a slash-prefixed key — invalidate both
  // families (same pattern as analytics-settings-modal) so stats refresh after a toggle.
  const dashboardSettingsKey = ["/api/analytics-settings", forUserId ?? ""];

  const calendarsQuery = useQuery<GhlCalendarInfo[]>({
    queryKey: ["ghl-conversation-calendars", forUserId ?? ""],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/integrations/crm/calendars${fuParam}`);
      return (res as { calendars?: GhlCalendarInfo[] }).calendars ?? [];
    },
    staleTime: 60_000,
    retry: false,
  });

  const settingsQuery = useQuery<AnalyticsSettingsSlice>({
    queryKey: settingsKey,
    queryFn: () => apiRequest("GET", settingsUrl),
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: (conversationCalendarIds: string[] | null) =>
      apiRequest(
        "PATCH",
        "/api/analytics-settings/conversation-calendars",
        forUserId
          ? { conversationCalendarIds, forClientUserId: forUserId }
          : { conversationCalendarIds }
      ),
    onMutate: async (conversationCalendarIds) => {
      await queryClient.cancelQueries({ queryKey: settingsKey });
      const previous = queryClient.getQueryData<AnalyticsSettingsSlice>(settingsKey);
      queryClient.setQueryData<AnalyticsSettingsSlice>(settingsKey, (old) => ({
        ...old,
        conversationCalendarIds,
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(settingsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: settingsKey });
      queryClient.invalidateQueries({ queryKey: dashboardSettingsKey });
    },
  });

  if (calendarsQuery.isLoading || settingsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-3 text-xs text-white/40">
        <Loader2 className="w-3 h-3 animate-spin" />
        {t("ghl.conversationCalendarsLoading")}
      </div>
    );
  }
  if (calendarsQuery.isError || settingsQuery.isError) {
    return (
      <div className="flex items-start gap-1.5 p-3 text-[11px] text-white/40">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        {t("ghl.conversationCalendarsError")}
      </div>
    );
  }
  const calendars = calendarsQuery.data ?? [];
  if (calendars.length === 0) return null;

  const selection = settingsQuery.data?.conversationCalendarIds ?? null;
  const allIds = calendars.map((c) => c.id);
  const checkedIds = new Set(selection ?? allIds);
  const noneSelected = checkedIds.size === 0;

  const toggle = (calendarId: string) => {
    const next = new Set(selection ?? allIds);
    if (next.has(calendarId)) next.delete(calendarId);
    else next.add(calendarId);
    // Everything checked again → store null so future GHL calendars count by default.
    const nextValue = allIds.every((id) => next.has(id)) ? null : [...next];
    saveMutation.mutate(nextValue);
  };

  const showLocation = new Set(calendars.map((c) => c.locationId ?? "")).size > 1;

  return (
    <div className="space-y-2 p-3 bg-white/[0.03] border border-white/[0.08] rounded-lg">
      <div className="text-[10px] uppercase tracking-wide text-white/40">
        {t("ghl.conversationCalendarsTitle")}
      </div>
      <p className="text-[11px] text-white/50">{t("ghl.conversationCalendarsHint")}</p>
      <div className="space-y-1.5">
        {calendars.map((cal) => (
          <label
            key={cal.id}
            className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1.5 cursor-pointer"
          >
            <Checkbox
              checked={checkedIds.has(cal.id)}
              onCheckedChange={() => toggle(cal.id)}
              disabled={saveMutation.isPending}
            />
            <span className="text-xs text-white/80 truncate">
              {cal.name}
              {showLocation && cal.locationName ? (
                <span className="text-white/40"> · {cal.locationName}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
      {noneSelected && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-400/90">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          {t("ghl.conversationCalendarsNone")}
        </div>
      )}
    </div>
  );
}
